<template>
  <Transition name="fade">
    <div v-if="isVisible" class="tilt-indicator">
      <div class="tilt-content">
        <div class="tilt-icon-wrapper">
          <SmartPhone01Icon class="tilt-phone" />

          <!-- Animated Arrows -->
          <div class="tilt-arrow tilt-arrow--up">
            <ArrowUp01Icon class="arrow-icon" />
          </div>
          <div class="tilt-arrow tilt-arrow--right">
            <ArrowRight01Icon class="arrow-icon" />
          </div>
          <div class="tilt-arrow tilt-arrow--down">
            <ArrowDown01Icon class="arrow-icon" />
          </div>
          <div class="tilt-arrow tilt-arrow--left">
            <ArrowLeft01Icon class="arrow-icon" />
          </div>
        </div>
        <span class="tilt-text">{{ $t('common.tiltToExplore') || 'Tilt to explore' }}</span>
      </div>
    </div>
  </Transition>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch } from 'vue'
import { gsap } from 'gsap'
import { useDevice } from '@/composables/useDevice'
import {
  ArrowUp01Icon,
  ArrowDown01Icon,
  ArrowLeft01Icon,
  ArrowRight01Icon,
  SmartPhone01Icon,
} from 'hugeicons-vue'

const props = defineProps({
  show: {
    type: Boolean,
    default: true,
  },
  duration: {
    type: Number,
    default: 4000,
  },
})

const { isDesktop } = useDevice()
const isVisible = ref(false)
let timeline = null

const initAnimation = () => {
  const phone = document.querySelector('.tilt-phone')
  const arrowUp = document.querySelector('.tilt-arrow--up')
  const arrowRight = document.querySelector('.tilt-arrow--right')
  const arrowDown = document.querySelector('.tilt-arrow--down')
  const arrowLeft = document.querySelector('.tilt-arrow--left')

  if (!phone) return

  if (timeline) timeline.kill()
  timeline = gsap.timeline({ repeat: -1, defaults: { ease: 'power1.inOut' } })

  const DURATION = 0.5
  const FADE_DUR = 0.25

  // Continuous Orbital Flow: Up -> Right -> Down -> Left -> (Loop to Up)
  timeline
    // 1. To UP (and hide Left)
    .addLabel('up')
    .to(phone, { rotationX: 30, rotationY: 0, duration: DURATION }, 'up')
    .to(arrowUp, { opacity: 1, duration: FADE_DUR }, 'up')
    .to(arrowLeft, { opacity: 0.2, duration: FADE_DUR }, 'up')

    // 2. To RIGHT (and hide Up)
    .addLabel('right')
    .to(phone, { rotationX: 0, rotationY: 30, duration: DURATION }, 'right')
    .to(arrowRight, { opacity: 1, duration: FADE_DUR }, 'right')
    .to(arrowUp, { opacity: 0.2, duration: FADE_DUR }, 'right')

    // 3. To DOWN (and hide Right)
    .addLabel('down')
    .to(phone, { rotationX: -30, rotationY: 0, duration: DURATION }, 'down')
    .to(arrowDown, { opacity: 1, duration: FADE_DUR }, 'down')
    .to(arrowRight, { opacity: 0.2, duration: FADE_DUR }, 'down')

    // 4. To LEFT (and hide Down)
    .addLabel('left')
    .to(phone, { rotationX: 0, rotationY: -30, duration: DURATION }, 'left')
    .to(arrowLeft, { opacity: 1, duration: FADE_DUR }, 'left')
    .to(arrowDown, { opacity: 0.2, duration: FADE_DUR }, 'left')
}

watch(
  () => props.show,
  (val) => {
    if (val && !isDesktop.value) {
      isVisible.value = true
      setTimeout(initAnimation, 300)

      // Auto hide after 5 seconds
      setTimeout(() => {
        isVisible.value = false
      }, 5000)
    } else {
      isVisible.value = false
      if (timeline) timeline.kill()
    }
  },
  { immediate: true }
)

onUnmounted(() => {
  if (timeline) timeline.kill()
})
</script>

<style scoped>
/* Tilt Indicator */
.tilt-indicator {
  @apply absolute bottom-8 left-1/2 -translate-x-1/2 z-50 pointer-events-none;
}

.tilt-content {
  @apply flex items-center gap-4 px-5 py-2.5 rounded-full;
  background: var(--color-deep-black);
  backdrop-filter: blur(12px);
  border: 1px solid var(--color-charcoal);
}

.tilt-icon-wrapper {
  @apply relative flex items-center justify-center w-10 h-10 text-[var(--color-electric-purple)];
  perspective: 800px;
  transform-style: preserve-3d;
}

.tilt-phone {
  @apply w-6 h-6;
  color: var(--color-electric-purple);
  filter: drop-shadow(0 0 12px rgba(124, 58, 237, 0.4));
  transform-origin: center center;
  will-change: transform;
}

.tilt-arrow {
  @apply absolute w-4 h-4 text-[var(--color-electric-purple)] opacity-20 pointer-events-none;
}

.arrow-icon {
  width: 100%;
  height: 100%;
}

.tilt-arrow--up {
  top: -4px;
  left: 50%;
  transform: translateX(-50%);
}

.tilt-arrow--down {
  bottom: -4px;
  left: 50%;
  transform: translateX(-50%);
}

.tilt-arrow--left {
  left: -4px;
  top: 50%;
  transform: translateY(-50%);
}

.tilt-arrow--right {
  right: -4px;
  top: 50%;
  transform: translateY(-50%);
}

.tilt-text {
  @apply text-[10px] font-semibold uppercase text-[var(--color-text-muted)];
}

/* Transitions */
.fade-enter-active,
.fade-leave-active {
  transition: all 0.6s cubic-bezier(0.16, 1, 0.3, 1);
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
